import React, { useState } from "react";

import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";
import { z } from "zod/v4-mini";

import { useRequestLoginCode } from "@/auth/auth.ts";
import { AUTH_ERROR_MAPPING, toAuthErrorData } from "@/auth/authErrors.ts";
import { formatPhone, identifierFrom, LoginChannel, oppositeChannel } from "@/auth/loginFlow.ts";
import { useStyles } from "@/hooks/useStyles";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { TextField } from "@/uiKit/atoms/TextField.tsx";
import { TextToast } from "@/uiKit/atoms/toast/TextToast.tsx";
import { ShowApiError } from "@/uiKit/molecules/ShowApiError.tsx";
import { cz } from "@/utils/zodUtils.ts";

const emailSchema = z.email();
const phoneSchema = cz.phone();

export type LoginIdentifierFormProps = {
  /** Pre-fills the field when the patient comes back from the code step. */
  initialChannel?: LoginChannel;
  initialContact?: string;
  onCodeSent: (channel: LoginChannel, contact: string) => void;
  onCancel: () => void;
};

/**
 * Asks for the phone or e-mail the login code should be sent to.
 */
export const LoginIdentifierForm: React.FC<LoginIdentifierFormProps> = ({
  initialChannel = "email",
  initialContact = "",
  onCodeSent,
  onCancel,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const requestCode = useRequestLoginCode();

  const [channel, setChannel] = useState<LoginChannel>(initialChannel);
  const [contact, setContact] = useState(initialContact);
  const [fieldError, setFieldError] = useState<string>();

  const isEmail = channel === "email";

  const onChangeContact = (value: string) => {
    setFieldError(undefined);
    setContact(isEmail ? value : formatPhone(value));
  };

  const switchChannel = () => {
    setChannel(oppositeChannel(channel));
    setContact("");
    setFieldError(undefined);
  };

  const submit = () => {
    const schema = isEmail ? emailSchema : phoneSchema;
    if (!schema.safeParse(isEmail ? contact.trim() : contact).success) {
      setFieldError(isEmail ? "errors.invalidEmail" : "errors.invalidPhone");
      return;
    }
    setFieldError(undefined);
    requestCode
      .request(identifierFrom(channel, contact))
      .then(() => {
        // Always advance: the api answers 200 whether or not the account exists.
        TextToast.success(t("pages.login.codeSentToast"));
        onCodeSent(channel, contact);
      })
      .catch(() => undefined); // Rendered by ShowApiError below.
  };

  return (
    <form
      noValidate
      className={styles.form}
      onSubmit={event => {
        event.preventDefault();
        submit();
      }}
    >
      <TextField
        fullWidth
        autoFocus
        value={contact}
        onChange={onChangeContact}
        label={t(isEmail ? "pages.login.emailLabel" : "pages.login.phoneLabel")}
        placeholder={t(isEmail ? "pages.login.emailPlaceholder" : "pages.login.phonePlaceholder")}
        name={isEmail ? "email" : "phone"}
        type={isEmail ? "email" : "tel"}
        inputMode={isEmail ? "email" : "tel"}
        autoComplete={isEmail ? "email" : "tel"}
        error={!!fieldError}
        helperText={fieldError && t(fieldError)}
      />
      <ShowApiError
        className={styles.apiError}
        error={toAuthErrorData(requestCode.error)}
        errorMapping={AUTH_ERROR_MAPPING}
      />
      <Button variant="contained" type="submit" fullWidth pending={requestCode.pending}>
        {t(isEmail ? "pages.login.requestEmailCode" : "pages.login.requestSmsCode")}
      </Button>
      <Button variant="text" className={styles.link} onClick={switchChannel}>
        {t(isEmail ? "pages.login.switchToSms" : "pages.login.switchToEmail")}
      </Button>
      <Button variant="text" className={styles.link} onClick={onCancel}>
        {t("pages.login.back")}
      </Button>
    </form>
  );
};

const makeStyles = (theme: Theme) => ({
  form: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing(6),
    padding: theme.spacing(12),
  }),
  apiError: css({
    height: "auto",
    minHeight: 0,
  }),
  link: css({
    alignSelf: "center",
    textDecoration: "underline",
  }),
});
